import React, { useState, useEffect, useCallback } from "react";

// region Imports - External libraries
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, CircularProgress, Chip, Tooltip
} from "@material-ui/core";
// endregion Imports - External libraries
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, ToastMessages, VehiclesModuleMessages, WaterFactorSourceTypeMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { HardwareWaterFactorVersionHistoryItemDto } from "@shared/dtos/hardware-water-factor-versions/hardware-water-factor-version-history-item.dto";
import { HardwareTypesID } from "@shared/constants/hardware-types.enum";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
// endregion Imports - Shared
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

interface WaterFactorHistoryTabProps {
  vehicle: Vehicle;
  isVisible: boolean;
}

const WaterFactorHistoryTab: React.FC<WaterFactorHistoryTabProps> = ({ vehicle, isVisible }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  // region States
  const [history, setHistory] = useState<HardwareWaterFactorVersionHistoryItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  // endregion States
  // region Constants
  const activeHardware = vehicle.hardwares?.find(h => h.active && h.type.id_hardware_type === HardwareTypesID.RASTREADOR);
  // endregion Constants
  // region Functions

  /**
   * Fetches the historical water factor versions data for the currently active hardware.
   *
   * This function retrieves data from the server endpoint and updates the application state with
   * the history data if the operation is successful. It handles loading state and displays an error
   * notification if the connection fails.
   *
   * Dependencies that trigger updates to this function include:
   * - `activeHardware`: The currently selected hardware object that contains `id_hardware`.
   * - `addToast`: A function to display notifications for errors or other alerts.
   * - `t`: A translation function for localized messages.
   *
   * The function performs the following steps:
   * 1. Checks if `activeHardware` is defined. If not, it exits early.
   * 2. Sets the `loading` state to true.
   * 3. Makes an API request to fetch the water factor versions history for the active hardware's ID.
   * 4. On a successful response, updates the `history` state with the retrieved data.
   * 5. If the API call fails, displays an error toast notification.
   * 6. Resets the `loading` state after the operation completes.
   *
   * @throws Will handle and display relevant error messages using toast notifications if the operation fails.
   */
  const fetchHistory = useCallback(async () => {
    if (!activeHardware) return;

    setLoading(true);
    try {
      const { data } = await api.get<StandardReturnObject<HardwareWaterFactorVersionHistoryItemDto[]>>(
        `/hardwares/water-factor-versions/history/${activeHardware.id_hardware}`
      );

      if (data.status === "success") {
        setHistory(data.result || []);
      }
    } catch (error) {
      addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
    } finally {
      setLoading(false);
    }
  }, [activeHardware, addToast, t]);

  /**
   * Asynchronously reverts a hardware configuration to a specified version.
   *
   * This function sends a POST request to revert a specific hardware configuration
   * by its version identifier. It handles success and error responses, displaying
   * appropriate toast messages based on the server's response or encountered issues.
   *
   * If successful, the hardware history will be refreshed.
   *
   * @param {string} versionId - The unique identifier of the version to revert to.
   *
   * @throws Will handle and display relevant error messages using toast notifications if the operation fails.
   */
  const handleRevert = async (versionId: string) => {
    if (!activeHardware) return;

    try {
      const { data } = await api.post(`/hardwares/water-factor-versions/revert/${versionId}`);

      if (data.status === "success") {
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        fetchHistory(); // Refresh history
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      if (error.response?.data?.message) {
        addToast({ type: "error", title: t(ToastMessages.error), description: error.response.data.message });
      } else {
        addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      }
    }
  };

  // endregion Functions
  // region Effects

  // Fetches history data when the tab becomes visible
  useEffect(() => {
    if (isVisible) {
      fetchHistory().then();
    }
  }, [isVisible, fetchHistory]);

  // endregion Effects

  if (loading) {
    return (
      <Container>
        <div className="loading-container">
          <CircularProgress />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(VehiclesModuleMessages.historyTabCurrentWaterFactor)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabPreviousWaterFactor)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabSourcedFrom)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabChangedByUser)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabChangedDate)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabStatus)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.historyTabActions)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((item) => (
              <TableRow key={item.id_hardware_water_factor_version}>
                <TableCell>{item.water_correction_factor}</TableCell>
                <TableCell>{item.previous_water_correction_factor || "-"}</TableCell>
                <TableCell>{t(WaterFactorSourceTypeMessages[item.source])}</TableCell>
                <TableCell>{item.created_by_user_name}</TableCell>
                <TableCell>{new Date(item.registration_date).toLocaleString()}</TableCell>
                <TableCell>
                  {item.active && <Chip label={t(VehiclesModuleMessages.historyTabActiveText)} size="small" className="active-chip" />}
                </TableCell>
                <TableCell>
                  <Tooltip title={item.active ? t(VehiclesModuleMessages.historyTabTooltipActiveText) : ""}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRevert(item.id_hardware_water_factor_version)}
                        disabled={item.active || !activeHardware}
                      >
                        {t(VehiclesModuleMessages.historyTabRevertButton)}
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default WaterFactorHistoryTab;
